import { z } from 'zod';

export const LanguageCodeSchema = z.enum(['en', 'es', 'fr', 'ru']);
export type LanguageCode = z.infer<typeof LanguageCodeSchema>;

export const RegisterSchema = z.enum(['formal', 'casual', 'auto']);
export type Register = z.infer<typeof RegisterSchema>;

export const EmotionSchema = z.enum(['neutral', 'happy', 'sad', 'angry', 'excited', 'fearful']);
export type Emotion = z.infer<typeof EmotionSchema>;

export const TranslationRequestSchema = z.strictObject({
  text: z.string().min(1).max(5000),
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
  // Required, no default (REQ-TRANS-002 AC-03): clients must send the register
  // toggle state explicitly ('auto' included) so signal-plumbing failures
  // surface as 400s instead of being silently masked.
  register: RegisterSchema,
});
export type TranslationRequest = z.infer<typeof TranslationRequestSchema>;

export const TranslationResponseSchema = z.object({
  translatedText: z.string(),
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
  register: RegisterSchema,
  confidence: z.number().min(0).max(1),
});
export type TranslationResponse = z.infer<typeof TranslationResponseSchema>;

/**
 * Client capture encoding declared on session_start so STT decodes correctly.
 * Optional for backward compatibility: server defaults to WEBM_OPUS (web).
 * Values align with Google Cloud Speech-to-Text AudioEncoding names used by
 * @zolotalk/voice-provider.
 */
export const VoiceAudioEncodingSchema = z.enum(['LINEAR16', 'OGG_OPUS', 'WEBM_OPUS', 'AMR_WB']);
export type VoiceAudioEncoding = z.infer<typeof VoiceAudioEncodingSchema>;

export const VoiceSessionConfigSchema = z.object({
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
  register: RegisterSchema.optional().default('auto'),
  /** Capture encoding for upstream audio (omit → server STT default WEBM_OPUS). */
  encoding: VoiceAudioEncodingSchema.optional(),
  /** Sample rate of capture stream when relevant (LINEAR16 / AMR_WB). */
  sampleRateHertz: z.number().int().positive().max(48_000).optional(),
});
export type VoiceSessionConfig = z.infer<typeof VoiceSessionConfigSchema>;

/**
 * Voice streaming session protocol (REQ-VOICE-001).
 *
 * A voice *session* is a server-side object that outlives any single
 * WebSocket connection: a dropped connection re-authenticates and resumes
 * the same session (`session_resume`) instead of starting over. Downstream
 * data frames carry a monotonic `seq` so a resuming client can tell the
 * server what it already received — the server then replays only newer
 * frames (AC-03: no already-delivered audio is replayed).
 */
export const VoiceSessionEndReasonSchema = z.enum(['client_end', 'max_duration', 'server_error']);
export type VoiceSessionEndReason = z.infer<typeof VoiceSessionEndReasonSchema>;

export const VoiceMessageSchema = z.discriminatedUnion('type', [
  // First message on every connect AND reconnect: in-band session
  // authentication (REQ-AUTH-001 AC-02). Browsers cannot set an
  // Authorization header on a WebSocket and tokens must never ride in URLs,
  // so the token travels as the opening frame.
  z.object({ type: z.literal('auth'), payload: z.object({ token: z.string().min(1).max(4096) }) }),
  z.object({ type: z.literal('session_start'), payload: VoiceSessionConfigSchema }),
  // Rebinds a fresh, re-authenticated connection to a live session.
  // `lastSeq` is the highest downstream seq the client received.
  z.object({
    type: z.literal('session_resume'),
    payload: z.object({
      sessionId: z.string().min(1).max(64),
      lastSeq: z.number().int().nonnegative(),
    }),
  }),
  z.object({ type: z.literal('audio'), payload: z.string() }),
  // End of the current utterance: flushes it through the pipeline.
  z.object({ type: z.literal('end') }),
  // Graceful client-initiated end of the whole session.
  z.object({ type: z.literal('session_end') }),
  z.object({ type: z.literal('ping') }),
]);
export type VoiceMessage = z.infer<typeof VoiceMessageSchema>;

export const VoiceResponseSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('auth_ok'), payload: z.undefined() }),
  z.object({
    type: z.literal('session_started'),
    payload: z.object({ sessionId: z.string(), maxDurationMs: z.number().int().positive() }),
  }),
  // `seq` is the server's current high-water mark; buffered frames above the
  // client's lastSeq follow immediately after this acknowledgment.
  z.object({
    type: z.literal('session_resumed'),
    payload: z.object({ sessionId: z.string(), seq: z.number().int().nonnegative() }),
  }),
  z.object({
    type: z.literal('session_ended'),
    payload: z.object({ reason: VoiceSessionEndReasonSchema }),
  }),
  z.object({
    type: z.literal('translation'),
    seq: z.number().int().positive(),
    payload: z.object({
      transcript: z.string(),
      translation: z.string(),
      audioBase64: z.string(),
      emotion: EmotionSchema.optional(),
    }),
  }),
  z.object({
    type: z.literal('error'),
    payload: z.object({ code: z.string(), message: z.string() }),
  }),
  z.object({ type: z.literal('pong'), payload: z.undefined() }),
]);
export type VoiceResponse = z.infer<typeof VoiceResponseSchema>;

export const OCRRequestSchema = z.object({
  imageBase64: z.string(),
  mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/heic']),
  targetLanguage: LanguageCodeSchema,
  register: RegisterSchema.optional().default('auto'),
});
export type OCRRequest = z.infer<typeof OCRRequestSchema>;

export const OCRResponseSchema = z.object({
  extractedText: z.string(),
  translatedText: z.string(),
  confidence: z.number().min(0).max(1),
  lowConfidenceRegions: z
    .array(z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() }))
    .optional(),
  retakeRecommended: z.boolean().optional(),
});
export type OCRResponse = z.infer<typeof OCRResponseSchema>;

export const HistoryEntrySchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  type: z.enum(['text', 'voice', 'ocr']),
  sourceLanguage: LanguageCodeSchema,
  targetLanguage: LanguageCodeSchema,
  sourceText: z.string(),
  translatedText: z.string(),
  register: RegisterSchema,
  createdAt: z.string().datetime(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type HistoryEntry = z.infer<typeof HistoryEntrySchema>;

export const VocabularyItemSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  term: z.string(),
  translation: z.string(),
  language: LanguageCodeSchema,
  sourceTranslationId: z.string().uuid(),
  createdAt: z.string().datetime(),
  masteryLevel: z.number().min(0).max(5).default(0),
});
export type VocabularyItem = z.infer<typeof VocabularyItemSchema>;

export const FlashcardSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  vocabularyItemId: z.string().uuid(),
  front: z.string(),
  back: z.string(),
  nextReviewAt: z.string().datetime(),
  interval: z.number().default(0),
  easeFactor: z.number().min(1.3).max(2.5).default(2.5),
  repetitions: z.number().default(0),
});
export type Flashcard = z.infer<typeof FlashcardSchema>;

export const DrillSessionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  vocabularyItemIds: z.array(z.string().uuid()),
  completedAt: z.string().datetime().optional(),
  score: z.number().min(0).max(1).optional(),
});
export type DrillSession = z.infer<typeof DrillSessionSchema>;

export const GameSessionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  gameType: z.enum(['word-match', 'fill-blank']),
  vocabularyItemIds: z.array(z.string().uuid()),
  score: z.number().min(0).max(1),
  completedAt: z.string().datetime(),
});
export type GameSession = z.infer<typeof GameSessionSchema>;

/**
 * Server-computed per-term accuracy row (FR-4.6 / REQ-LEARN-002).
 * Only terms with at least one attempt are included — never fabricate 0%.
 */
export const TermAccuracyEntrySchema = z.object({
  vocabularyItemId: z.string().uuid(),
  accuracy: z.number().min(0).max(1),
  attemptCount: z.number().int().positive(),
});
export type TermAccuracyEntry = z.infer<typeof TermAccuracyEntrySchema>;

/** Bounded page of per-term accuracy rows (AC-05). */
export const TermAccuracyPageSchema = z.object({
  items: z.array(TermAccuracyEntrySchema),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});
export type TermAccuracyPage = z.infer<typeof TermAccuracyPageSchema>;

export const LearningProgressSchema = z.object({
  userId: z.string().uuid(),
  dailyStreak: z.number().int().nonnegative().default(0),
  cardsReviewed: z.number().int().nonnegative().default(0),
  accuracy: z.number().min(0).max(1).default(0),
  lastReviewedAt: z.string().datetime().optional(),
  /**
   * Optional for rollout ordering: existing clients that ignore the field keep
   * working; fail-closed responses may omit it on system error (REQ-LEARN-002).
   */
  termAccuracy: TermAccuracyPageSchema.optional(),
});
export type LearningProgress = z.infer<typeof LearningProgressSchema>;

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string().min(1).max(100),
  avatarUrl: z.string().url().optional(),
  createdAt: z.string().datetime(),
  subscriptionTier: z.enum(['free', 'pro']).default('free'),
});
export type User = z.infer<typeof UserSchema>;

export const ErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
  correlationId: z.string().uuid(),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

export const PaginationSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
});
export type Pagination = z.infer<typeof PaginationSchema>;

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
  });
